import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { Box } from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import { useIsMutating } from '@tanstack/react-query'

import { isUserRole } from '@/types/userStore.types'

import { loginFormSchema } from '@/modules/login/components/LoginForm/LoginForm.schema'
import type { LoginFormSchema } from '@/modules/login/components/LoginForm/LoginForm.types'
import { loginFormDefaultValues } from '@/modules/login/components/LoginForm/LoginForm.values'
import type { LoginLocationState } from '@/modules/login/routes/loginRouteConfig.types'
import { PasswordResetRoutePathsEnum } from '@/modules/passwordReset/routes/passwordResetRouteConfig.types'
import { RegisterRoutePathsEnum } from '@/modules/register/routes/registerRouteConfig.types'

import { useUserStore } from '@/store/user.store'

import { Anchor } from '@/components/Anchor/Anchor'
import { Button } from '@/components/Button/Button'
import { PasswordInput } from '@/components/PasswordInput/PasswordInput'
import { Text } from '@/components/Text/Text'
import { TextInput } from '@/components/TextInput/TextInput'

import { useRolanWealthServiceAuthControllerLogin } from '@/__generated__/queries'
import { role as Roles } from '@/__generated__/requests'

import { getUserDefaultPath } from '@/helpers/getUserDefaultPath.helper'

export function LoginForm() {
  const setUser = useUserStore.use.setUser()
  const setCurrentRole = useUserStore.use.setCurrentRole()
  const user = useUserStore.use.user()
  const currentRole = useUserStore.use.currentRole()

  const navigate = useNavigate()
  const location = useLocation()

  const from = (location.state as LoginLocationState | null)?.from
  const redirectPath = from
    ? `${from.pathname}${from.search}${from.hash}`
    : null

  const isMutating = useIsMutating({
    exact: true,
  })

  const form = useForm<LoginFormSchema>({
    initialValues: loginFormDefaultValues,
    validate: zodResolver(loginFormSchema),
  })

  const loginMutation = useRolanWealthServiceAuthControllerLogin({
    onSuccess: data => {
      setUser(data)

      const role = data.roles?.includes(Roles.EMPLOYEE)
        ? Roles.EMPLOYEE
        : (data.roles?.[0] ?? null)

      setCurrentRole(role && isUserRole(role) ? role : null)

      const route =
        redirectPath ??
        getUserDefaultPath(data, role && isUserRole(role) ? role : null)

      navigate(route, { replace: true, state: { fromLogin: true } })
    },
  })

  const handleSubmit = form.onSubmit(values => {
    loginMutation.mutate({
      requestBody: values,
    })
  })

  if (user) {
    return (
      <Navigate
        replace
        state={{ fromLogin: true }}
        to={redirectPath ?? getUserDefaultPath(user, currentRole)}
      />
    )
  }

  return (
    <Box component="form" noValidate onSubmit={handleSubmit}>
      <Box mt={48}>
        <TextInput
          label="Email"
          placeholder="Enter your email address"
          {...form.getInputProps('email')}
        />

        <PasswordInput
          label="Password"
          placeholder="Enter your password"
          {...form.getInputProps('password')}
        />
      </Box>

      <Box style={{ textAlign: 'right' }}>
        <Anchor
          href={PasswordResetRoutePathsEnum.PASSWORD_RESET}
          variant="secondary"
        >
          Forgot password
        </Anchor>
      </Box>

      <Box mt={56}>
        <Button loading={!!isMutating} type="submit">
          Sign in
        </Button>
      </Box>

      <Box mt={24}>
        <Text>
          Don&apos;t have an account?{' '}
          <Anchor href={RegisterRoutePathsEnum.REGISTER} variant="primary">
            Sign up
          </Anchor>
        </Text>
      </Box>
    </Box>
  )
}
